/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { Grid, Stack } from "@mui/material";
import FormField from "components/generals/FormInput";
import FormikForm from "components/layout/FormikForm";
import { Form, FormikProps } from "formik";
import {
  HeaderEnum,
  QueryResultInterface,
  TemplateContentTypeEnum,
  TemplateInterface,
  TemplateTypeEnum,
} from "@malmur-technologies/medicalsystem-shared";
import useHttp from "hooks/http.hook";
import { FormProps, TagsInterface, OptionColor } from "interfaces";
import BadgeIcon from "@mui/icons-material/Badge";
import { H4 } from "components/generals/_Titles";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { CoreAPI_URLs } from "constants/http.const";
import { Method } from "axios";
import { useTranslation } from "react-i18next";
import Button from "components/generals/Button";
import FormTextEditor from "components/generals/FormTextEditor";
import Box from "components/generals/Box";
import FormActionButtons from "./custom/FormActionButtons";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { useEffect, useState } from "react";
import AutocompleteCheckboxes from "components/generals/AutocompleteCheckboxes";
import { processTemplateTags, handleAddNewTag } from "utils/template.utils";

/**
 * Prescription template form component for creating and managing reusable prescription templates.
 * 
 * Features:
 * - Create/edit prescription templates with HTML content
 * - Rich text editor for prescription content
 * - Tag-based template organization and filtering
 * - Custom tag creation and management
 * - Multi-select tag assignment with autocomplete checkboxes
 * - Template versioning (version 1.0)
 * - Content type: TEXT_HTML
 * - Template type: PRESCRIPTION
 * - Automatic tag inheritance from filter context
 * - Color-coded tag visualization
 * - Permission-based access control
 * 
 * Tag Management:
 * - Select from existing tags
 * - Add new custom tags
 * - Tags persist across template instances
 * - First tag is the custom/primary tag
 * - Additional tags inherited from filterTag prop
 * 
 * Template Organization:
 * - Templates grouped by tags for easy discovery
 * - Filter templates by tag in parent views
 * - Supports hierarchical tag relationships
 * 
 * Use Cases:
 * - Create standard prescription templates for common medications
 * - Build procedure-specific prescription sets
 * - Standardize prescription format across providers
 * - Quick prescription generation from templates
 * 
 * Security:
 * - Permission-based rendering via PermissionWrapper
 * - Validates entity permissions for edit/delete
 * - Uses HeaderEnum.RESOURCE_ID for secure identification
 * - HTML content sanitization in rich text editor
 * 
 * @param {FormProps<TemplateInterface>} props - Form properties
 * @param {TemplateInterface} props.model - Template data to edit or empty model for new
 * @param {FormActionType} props.action - Form action (NEW, MODIFY, READ_ONLY)
 * @param {Function} props.onCloseAction - Callback when form closes
 * @param {Function} props.setModelList - Callback to update template list
 * @param {TemplateInterface[]} props.modelList - Current list of templates
 * @param {TagsInterface[]} props.filterTag - Optional tags to filter/pre-select
 * 
 * @returns {JSX.Element} Prescription template form with rich text editor and tag management
 */
const PrescriptionTemplateForm: React.FC<FormProps<TemplateInterface>> = ({
  model,
  action,
  onCloseAction,
  readOnlyValue,
  setModelList,
  modelList,
  filterTag,
}) => {
  const [isLoading, _error, sendRequest] = useHttp();
  const [optionColors, setOptionColors] = useState<OptionColor[]>([]);
  const [PrescriptionListResponse, setPrescriptionListResponse] = useState<TagsInterface[]>([]);
  const [selectedPrescriptions, setSelectedPrescriptions] = useState<TagsInterface[]>([]);
  const { t } = useTranslation();
  const [newTag, setNewTag] = useState("");
  const customTag = model.tags[0];

  const onGetPrescriptionHandler = (response: QueryResultInterface<TemplateInterface>) => {
    const { availableTags, selectedTags } = processTemplateTags(response, filterTag);
    setPrescriptionListResponse(availableTags);
    setSelectedPrescriptions(selectedTags);
  };

  const handleSelectChange = (tags: TagsInterface[]) => {
    setSelectedPrescriptions(tags);
  };

  useEffect(() => {
    sendRequest(CoreAPI_URLs.HPI_TEMPLATE_GET_ALL, onGetPrescriptionHandler, "GET");
    if (filterTag && filterTag.length > 0) {
      setSelectedPrescriptions(filterTag);
    }
  }, []);

  const submitHandler = (model: Partial<TemplateInterface>) => {
    model.version = "1.0";
    model.contentType = TemplateContentTypeEnum.TEXT_HTML;
    model.type = TemplateTypeEnum.PRESCRIPTION;
    if (model.tags.length == 1 && filterTag && filterTag.length > 0) {
      model.tags = [customTag, ...filterTag.map((tag) => tag.name)];
    }
    if (!model.tags.includes(customTag)) {
      model.tags = [customTag, ...model.tags];
    }
    const method: Method = action === FormActionType.NEW ? "POST" : "PUT";
    sendRequest(
      CoreAPI_URLs.HPI_TEMPLATE_GENERAL_OPERATIONS,
      onPrescriptionTemplateUpdateHandler,
      method,
      JSON.stringify(model),
      [{ header: HeaderEnum.RESOURCE_ID, value: model.id }],
      { successMessage: t("prescriptionTemplateForm.templateSavedSuccessfully") }
    );
  };

  const handleAddTag = () => {
    const { updatedAvailableTags, updatedSelectedTags } = handleAddNewTag(
      newTag,
      PrescriptionListResponse,
      selectedPrescriptions
    );

    setPrescriptionListResponse(updatedAvailableTags);
    setSelectedPrescriptions(updatedSelectedTags);
    setNewTag("");
    return updatedSelectedTags;
  };

  const onPrescriptionTemplateUpdateHandler = (Prescriptiontemplate: TemplateInterface) => {
    if (action === FormActionType.MODIFY) {
      setModelList(
        modelList.map((model) =>
          model.id === Prescriptiontemplate.id ? Prescriptiontemplate : model
        )
      );
    } else {
      setModelList((oldArray) => [...oldArray, Prescriptiontemplate]);
    }
    onCloseAction();
  };

  return (
    <FormikForm
      onSubmit={(model) => {
        submitHandler(model);
      }}
      initialValues={model}
    >
      {({ values, errors, touched, setFieldValue }: FormikProps<Partial<TemplateInterface>>) => (
        <Form id="prescriptionTemplateForm" autoComplete="true">
          <Stack alignItems="center" direction="row" gap={2} mb={3}>
            <BadgeIcon fontSize="large" />
            <H4>{t("prescriptionTemplateForm.prescriptionTemplateData")}</H4>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormField
                readOnly={action === FormActionType.NEW ? false : readOnlyValue}
                type="text"
                label={t("prescriptionTemplateForm.title")}
                name="title"
                value={values.title || ""}
                error={errors.title && touched.title}
              />
            </Grid>
            <Grid item xs={12}>
              <FormTextEditor
                readOnly={action === FormActionType.NEW ? false : readOnlyValue}
                setFieldValue={(val) => setFieldValue("template", val)}
                value={values.template}
                editorHeight="30rem"
                wrapperHeight="40rem"
              />
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <FormField
                readOnly={true}
                type="text"
                label={t("prescriptionTemplateForm.tags")}
                name="customTags"
                value={t(`prescriptionTemplateForm.${customTag.replace(/\s+/g, '')}`)}
                error={errors.tags && touched.tags}
              />
            </Grid>

            <Grid item xs={6} md={6} lg={6}>
              <Box>
                <FormField
                  readOnly={true}
                  type="text"
                  label={t("prescriptionTemplateForm.type")}
                  name="type"
                  value={t("prescriptionTemplateForm.prescription")}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6} mb={1}>
            <Grid container direction="row" spacing={2} mb={2} alignItems="center">
                <Grid item xs={9}>
                  <FormField
                    readOnly={action === FormActionType.NEW ? false : readOnlyValue}
                    type="text"
                    label={t("prescriptionTemplateForm.tags")}
                    name="newTag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                  />
                </Grid>
                <Grid item>
                  {(action === FormActionType.NEW || !readOnlyValue) && (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => {
                        const updatedSelection = handleAddTag();
                        setFieldValue(
                          "tags",
                          updatedSelection.map((option) => option.name)
                        );
                      }}
                      size="small"
                    >
                      {t("prescriptionTemplateForm.addTag")}
                    </Button>
                  )}
                </Grid>
              </Grid>
              {(action === FormActionType.NEW || !readOnlyValue) && (
                <AutocompleteCheckboxes
                  options={PrescriptionListResponse}
                  label={t("prescriptionTemplateForm.selectTags")}
                  placeholder={t("prescriptionTemplateForm.chooseTags")}
                  onChange={(selected) => {
                    handleSelectChange(selected);
                    setFieldValue(
                      "tags",
                      selected.map((option) => option.name)
                    );
                  }}
                  value={selectedPrescriptions}
                  optionColors={optionColors}
                  setOptionColors={setOptionColors}
                  getOptionLabel={(option) => option.name}
                />
              )}
            </Grid>
          </Grid>
          <Stack alignItems="center" direction="row" gap={30}>
            {readOnlyValue === false && (
              <div>
                {action === FormActionType.NEW ? (
                  <PermissionWrapper
                    requiredPermissions={[PermissionsUser[EntityPermissions.PRESCRIPTIONS].CREATE]}
                  >
                    <Button type="submit" variant="contained" color="info" disabled={isLoading}>
                      {t("stepperFormActionButtons.submit")}
                    </Button>
                  </PermissionWrapper>
                ) : (
                  <PermissionWrapper
                    requiredPermissions={[PermissionsUser[EntityPermissions.PRESCRIPTIONS].EDIT]}
                  >
                    <Button type="submit" variant="contained" color="info" disabled={isLoading}>
                      {t("stepperFormActionButtons.update")}
                    </Button>
                  </PermissionWrapper>
                )}
              </div>
            )}

            {readOnlyValue === true && (
              <div>
                {action === FormActionType.NEW ? (
                  <PermissionWrapper
                    requiredPermissions={[PermissionsUser[EntityPermissions.PRESCRIPTIONS].CREATE]}
                  >
                    <Button type="submit" variant="contained" color="info" disabled={isLoading}>
                      {t("stepperFormActionButtons.submit")}
                    </Button>
                  </PermissionWrapper>
                ) : null}
              </div>
            )}
          </Stack>
          <FormActionButtons showSubmitButton={false} closeAction={onCloseAction} />
        </Form>
      )}
    </FormikForm>
  );
};

export default PrescriptionTemplateForm;
